import React from 'react';
import { getTariffName } from '@/components/Account/Tariffs/utils';
import { useTariffLibrary } from './hooks';
import GroupTabs from './GroupTabs';
import CommodityChips from './CommodityChips';
import TariffSection from './TariffSection';
import TariffRow from './TariffRow';
import AddTariffModal from './AddTariffModal';
import DeleteTariffModal from './DeleteTariffModal';
import { Library } from './blocks';

/**
 * Tariff list grouped into category sections, with group tabs and commodity filter chips. Each
 * page keeps its own create / duplicate / delete mutations; the library only saves category and
 * commodity edits.
 *
 * @param {Object[]} tariffs - Order tariffs; each list query must spread TariffCategorizationFields.
 * @param {boolean} loading - Disables copy and delete while the page's list or delete is in flight.
 * @param {Object[]} algoTypeOptions - `{ value, label }` choices for the Add Tariff Type field.
 * @param {Function} onCreate - Receives insert-ready `{ algo_type, category, commodity_tags }`.
 * @param {Function} onDuplicate - Receives a tariff_id.
 * @param {Function} onDelete - Receives a tariff_id, after the user confirms.
 * @param {Function} [renderRowMeta] - Muted, non-interactive text shown after the tariff name.
 * @param {Function} [renderRowActions] - Extra controls shown before the row's icons.
 * @param {boolean} [showAssignments] - Adds an "Assigned to" line, listing the accounts on the
 *   tariff's pricing overrides, to an opened row. Admin pages only.
 */
const TariffLibrary = ({
    tariffs,
    loading,
    algoTypeOptions,
    onCreate,
    onDuplicate,
    onDelete,
    renderRowMeta,
    renderRowActions,
    showAssignments = false,
}) => {
    const { state, callbacks } = useTariffLibrary({ tariffs, onDelete });

    return (
        <Library>
            <AddTariffModal
                open={state.addOpen}
                algoTypeOptions={algoTypeOptions}
                onClose={callbacks.closeAdd}
                onCreate={onCreate}
            />
            <DeleteTariffModal
                open={!!state.deleteTarget}
                onCancel={callbacks.cancelDelete}
                onConfirm={callbacks.confirmDelete}
            />
            <GroupTabs value={state.group} onChange={callbacks.setGroup} onAdd={callbacks.openAdd} />
            <CommodityChips
                counts={state.counts}
                selected={state.selectedCommodities}
                onToggle={callbacks.toggleSelectedCommodity}
            />
            {state.sections.map((section) => (
                <TariffSection key={section.key} label={section.label} empty={!section.tariffs.length}>
                    {section.tariffs.map((tariff) => (
                        <TariffRow
                            key={tariff.tariff_id}
                            tariff={tariff}
                            name={getTariffName(tariff, state.nameIndexById[tariff.tariff_id])}
                            expanded={state.expandedId === tariff.tariff_id}
                            disabled={loading}
                            showAssignments={showAssignments}
                            meta={renderRowMeta ? renderRowMeta(tariff) : null}
                            actions={renderRowActions ? renderRowActions(tariff) : null}
                            onToggle={() => callbacks.toggleExpanded(tariff.tariff_id)}
                            onEdit={() => callbacks.editTariff(tariff.tariff_id)}
                            onDuplicate={() => onDuplicate(tariff.tariff_id)}
                            onDelete={() => callbacks.requestDelete(tariff)}
                            onCategoryChange={(category) => callbacks.setCategory(tariff, category)}
                            onCommodityToggle={(value) => callbacks.toggleCommodity(tariff, value)}
                        />
                    ))}
                </TariffSection>
            ))}
        </Library>
    );
};

export default TariffLibrary;
